import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { ApiInventoryHelpDataResponse, BarLevelStatus } from "@/app/types/inventory.types";
import type { Category } from "@/shared/types";
import type { OpeningBalanceItemPayload, OpeningBalanceItem } from "@/features/Inventory/components/types/OpeningBalanceTypes";

interface InventoryState {
  categories: Category[];
  activeStatusFilter: BarLevelStatus;
  InventoryHelpData: ApiInventoryHelpDataResponse | null
  isInventoryHelpDataLoaded: boolean
  openingBalanceDraft: OpeningBalanceItemPayload[] | null
  enteredOpeningBalanceItems: OpeningBalanceItem[]
}

const initialState: InventoryState = {
  categories: [],
  activeStatusFilter: "all",
  InventoryHelpData: null,
  isInventoryHelpDataLoaded: false,
  openingBalanceDraft: null,
  enteredOpeningBalanceItems: [],
};

const inventorySlice = createSlice({
  name: "inventory",
  initialState,
  reducers: {
    setInventoryCategories: (state, action: PayloadAction<Category[]>) => {
      state.categories = action.payload;
    },
    setActiveInventoryStatusFilter: (
      state,
      action: PayloadAction<BarLevelStatus>,
    ) => {
      state.activeStatusFilter = action.payload;
    },
    clearActiveInventoryStatusFilter: (state) => {
      state.activeStatusFilter = "all";
    },
    setInventoryHelpData: (state, action: PayloadAction<ApiInventoryHelpDataResponse>) => {
      state.InventoryHelpData = action.payload;
    },
    setIsInventoryHelpDataLoaded: (state, action: PayloadAction<boolean>) => {
      state.isInventoryHelpDataLoaded = action.payload;
    },
    clearInventoryHelpData: (state) => {
      state.InventoryHelpData = null;
      state.isInventoryHelpDataLoaded = false;
    },
    setOpeningBalanceDraft: (state, action: PayloadAction<OpeningBalanceItemPayload[]>) => {
      state.openingBalanceDraft = action.payload;
    },
    clearOpeningBalanceDraft: (state) => {
      state.openingBalanceDraft = null;
    },
    setEnteredOpeningBalanceItems: (state, action: PayloadAction<OpeningBalanceItem[]>) => {
      const existingItems = state.enteredOpeningBalanceItems;
      const newItems = action.payload;
      
      const itemsMap = new Map<number, OpeningBalanceItem>();
      existingItems.forEach(item => itemsMap.set(item.id, item));
      newItems.forEach(item => {
        // Only add/update if item has data
        if (item.total_qty_base !== 0 && Number(item.total_qty_base) > 0) {
          itemsMap.set(item.id, item);
        } else {
          // If it's empty, remove it
          itemsMap.delete(item.id);
        }
      });
      state.enteredOpeningBalanceItems = Array.from(itemsMap.values());
    },
    clearEnteredOpeningBalanceItems: (state) => {
      state.enteredOpeningBalanceItems = [];
    },
  },
});

export const {
  setInventoryCategories,
  setActiveInventoryStatusFilter,
  clearActiveInventoryStatusFilter,
  setInventoryHelpData,
  setIsInventoryHelpDataLoaded,
  clearInventoryHelpData,
  setOpeningBalanceDraft,
  clearOpeningBalanceDraft,
  setEnteredOpeningBalanceItems,
  clearEnteredOpeningBalanceItems,
} = inventorySlice.actions;

export const inventoryReducer = inventorySlice.reducer;

export const selectInventoryCategories = (state: { inventory: InventoryState }) =>
  state.inventory.categories;

export const selectActiveInventoryStatusFilter = (state: {
  inventory: InventoryState;
}) => state.inventory.activeStatusFilter;

export const selectInventoryHelpData = (state: { inventory: InventoryState }) =>
  state.inventory.InventoryHelpData;

export const selectIsInventoryHelpDataLoaded = (state: { inventory: InventoryState }) =>
  state.inventory.isInventoryHelpDataLoaded;

export const selectOpeningBalanceDraft = (state: { inventory: InventoryState }) =>
  state.inventory.openingBalanceDraft;

export const selectEnteredOpeningBalanceItems = (state: { inventory: InventoryState }) =>
  state.inventory.enteredOpeningBalanceItems;
